import { noop } from "@tanstack/react-query"
import { createFileRoute, notFound, redirect } from "@tanstack/react-router"

import { $rqIotApiClient } from "@/api/iot"
import { getBoardsQueryOptions } from "@/api/query/boardsQueryOptions"
import { propertyTypesQuery } from "@/api/query/propertyTypes"
import { getXOrganizationHeaders } from "@/api/utils"
import { ensureValidQueryData } from "@/api/utils/ensureValidQueryData"
import { routeGuard } from "@/lib/routing/beforeLoad"
import { recentsStore } from "@/store/recents"

export const Route = createFileRoute("/_authlayout/_header/things/$id")({
  // Building the query is pure, so it can live here: descendants at any depth
  // share one definition derived from the current space and params.
  beforeLoad: async ({ context, params }) => {
    await routeGuard(context)

    // Kick these off alongside the thing fetch below instead of after it resolves -- neither
    // depends on the thing's data, only on the already-known route params/space.
    const space = context.spaceStorage.getSpace()
    const thingQueryOptions = $rqIotApiClient.queryOptions("get", `/iot/v2/things/{id}`, {
      params: { header: getXOrganizationHeaders(space), path: { id: params.id } }
    })

    void context.queryClient
      .query(
        $rqIotApiClient.queryOptions("get", `/iot/v2/things/{id}/properties`, {
          params: { header: getXOrganizationHeaders(space), path: { id: params.id } }
        })
      )
      .catch(noop)
    void context.queryClient.query(propertyTypesQuery).catch(noop)

    return { thingQueryOptions }
  },
  loader: async ({ context: { queryClient, thingQueryOptions }, params }) => {
    // Must be read before validating, since a determinate 4xx evicts the query. `cause`
    // cannot stand in for this: it tracks the route slot, so navigating from one
    // thing straight to a bogus id is still `"stay"`.
    const wasCached = queryClient.getQueryData(thingQueryOptions.queryKey) !== undefined

    const [{ data: thing, error }] = await Promise.all([
      ensureValidQueryData(thingQueryOptions),
      queryClient.query({ ...getBoardsQueryOptions(), staleTime: "static" })
    ])

    if (error) {
      // Try purging recents in case the thing was deleted
      recentsStore.getState().removeRecent(params.id)

      // Deleted under us → back to the list. Otherwise a bad id → not found.
      if (wasCached) throw redirect({ to: "/things" })
      throw notFound()
    }

    return { thing }
  },
  head: ctx => ({
    meta: [
      {
        title: `${ctx.loaderData?.thing.name || "Thing"} | Arduino Cloud`
      }
    ]
  })
})
